import type { MultiSelectOption } from "@/components/ui/MultiSelect";

export interface EventContent {
  id: string;
  name: string;
}

export interface Coupon {
  id: string;
  name?: string;
  promoCode?: string;
  contentIds?: string[];
  value?: string | number;
  unit?: string; // e.g. "PERCENTAGE" | "FIXED_AMOUNT"
  startDate?: string; // "2026-07-21T00:00:00.000Z"
  endDate?: string;
  oneUsePerCompany?: boolean;
  oneUsePerUser?: boolean;
  usedByUserIds?: string[];
  usedByCompanyMembershipIds?: string[];
  status?: string;
}

export type DiscountType = "PERCENTAGE" | "FIXED_AMOUNT";

export interface CouponFormValues {
  id?: string;
  internalCodeName: string;
  promoCode: string;
  contentIds: string[];
  discountType: DiscountType;
  value: string;
  startDate: string; // yyyy-mm-dd, for <input type="date">
  endDate: string; // yyyy-mm-dd, optional
  oneUsePerCompany: boolean;
  oneUsePerUser: boolean;
  employeeCode: boolean;
  eventId?: string; // NOTE: not part of the raw Coupon, but we need it for create/update mutations
}

export const DISCOUNT_TYPE_OPTIONS: { value: DiscountType; label: string }[] = [
  { value: "PERCENTAGE", label: "Percentage (%)" },
  { value: "FIXED_AMOUNT", label: "Fixed Amount" },
];

export function todayISODate(): string {
  const parts = new Date().toISOString().split("T");
  return parts[0] ?? "";
}

export function toDateOnly(iso?: string): string {
  if (!iso) return "-";
  const parts = iso.split("T");
  return parts[0] ?? "-";
}

// "FIXED_AMOUNT" -> "Fixed Amount", "percentage" -> "Percentage"
export function normalizeLabel(text?: string): string {
  if (!text) return "-";
  return text
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function formatCouponValue(
  value: string | number | undefined,
  unit: string | undefined,
): string {
  if (value === undefined || value === null || value === "") return "-";
  return unit?.toLowerCase() === "percentage" ? `${value}%` : `${value}`;
}

export function getContentNames(
  contentIds: string[] | undefined,
  eventContents: EventContent[] | undefined,
): string {
  if (!contentIds?.length || !eventContents?.length) return "-";
  const names = contentIds
    .map((id) => eventContents.find((c) => c.id === id)?.name)
    .filter((n): n is string => Boolean(n));
  return names.length ? names.join(", ") : "-";
}

export function eventContentsToOptions(
  eventContents: EventContent[] | undefined,
): MultiSelectOption[] {
  return (eventContents ?? []).map((c) => ({ value: c.id, label: c.name }));
}

export const DEFAULT_COUPON_FORM: CouponFormValues = {
  internalCodeName: "",
  promoCode: "",
  contentIds: [],
  discountType: "PERCENTAGE",
  value: "",
  startDate: todayISODate(),
  endDate: "",
  oneUsePerCompany: false,
  oneUsePerUser: false,
  employeeCode: false,
};

// NOTE: there's no distinct "employeeCode" field on the raw Coupon in what
// we have visibility into — same as the table's derived column, we infer
// the initial checkbox state from the two single-use flags. If/when the API
// exposes a real `employeeCode` field, swap this for `!!coupon.employeeCode`.
export function couponToFormValues(coupon: Coupon): CouponFormValues {
  return {
    id: coupon.id,
    internalCodeName: coupon.name ?? "",
    promoCode: coupon.promoCode ?? "",
    contentIds: coupon.contentIds?.slice(0, 1) ?? [],
    discountType: (coupon.unit?.toUpperCase() as DiscountType) || "PERCENTAGE",
    value:
      coupon.value !== undefined && coupon.value !== null
        ? String(coupon.value)
        : "",
    startDate: coupon.startDate ? toDateOnly(coupon.startDate) : todayISODate(),
    endDate: coupon.endDate ? toDateOnly(coupon.endDate) : "",
    oneUsePerCompany: !!coupon.oneUsePerCompany,
    oneUsePerUser: !!coupon.oneUsePerUser,
    employeeCode: !!coupon.oneUsePerCompany && !!coupon.oneUsePerUser,
  };
}
